import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  Class,
  Employee,
  Location,
  MessageType,
  ProvinceState,
  ServiceCode,
  TechnicianGroup,
  TransportOption,
  WarrantySubmissionType,
  VehicleColour as Colour,
  SubType,
  VehicleInspectionWearPattern,
  VehicleMake,
} from '@quorum/models/xs-resource';
import {
  associateClassesAdapter,
  declinedReasonsAdapter,
  directMessageTemplatesAdapter,
  employeesAdapter,
  locationsAdapter,
  messageTypesAdapter,
  provinceStatesAdapter,
  serviceCodesAdapter,
  ServiceLaneDropdownsState,
  SERVICELANEDROPDOWNS_FEATURE_KEY,
  submissionTypeAdapter,
  technicianGroupsAdapter,
  transportOptionsAdapter,
  vehicleColourAdapter,
  vehicleInspectionWearPatternsAdapter,
  vehicleMakesAdapter,
  vehicleSubTypeAdapter,
} from './service-lane-dropdowns.reducer';

// Lookup the 'ServiceLaneDropdowns' feature state managed by NgRx
const getServiceLaneDropdownsState = createFeatureSelector<ServiceLaneDropdownsState>(SERVICELANEDROPDOWNS_FEATURE_KEY);

const locations = createSelector(getServiceLaneDropdownsState, (state: ServiceLaneDropdownsState) => state.locations);

const locationSelectors = locationsAdapter.getSelectors(locations);

const messageTypes = createSelector(
  getServiceLaneDropdownsState,
  (state: ServiceLaneDropdownsState) => state.messageTypes
);
const messageTypeSelectors = messageTypesAdapter.getSelectors(messageTypes);

const associateClasses = createSelector(
  getServiceLaneDropdownsState,
  (state: ServiceLaneDropdownsState) => state.associateClasses
);
const associateClassSelectors = associateClassesAdapter.getSelectors(associateClasses);

const serviceCodes = createSelector(
  getServiceLaneDropdownsState,
  (state: ServiceLaneDropdownsState) => state.serviceCodes
);
const serviceCodeSelectors = serviceCodesAdapter.getSelectors(serviceCodes);

const activeServiceCodes = createSelector(serviceCodeSelectors.selectAll, (state: ServiceCode[]) => {
  return state.filter((serviceCode) => {
    return (serviceCode.activeFlag === '1' && serviceCode.shopDepartment === '1') || serviceCode.shopDepartment === 'M';
  });
});

const provinceStates = createSelector(
  getServiceLaneDropdownsState,
  (state: ServiceLaneDropdownsState) => state.provinceStates
);
const provinceStateSelectors = provinceStatesAdapter.getSelectors(provinceStates);

const employees = createSelector(getServiceLaneDropdownsState, (state: ServiceLaneDropdownsState) => state.employees);
const employeesSelectors = employeesAdapter.getSelectors(employees);

const directMessageTemplates = createSelector(
  getServiceLaneDropdownsState,
  (state: ServiceLaneDropdownsState) => state.directMessageTemplates
);

const directMessageTemplatesSelectors = directMessageTemplatesAdapter.getSelectors(directMessageTemplates);

const transportOptions = createSelector(
  getServiceLaneDropdownsState,
  (state: ServiceLaneDropdownsState) => state.transportOptions
);
const transportOptionsSelectors = transportOptionsAdapter.getSelectors(transportOptions);

const declinedReasons = createSelector(
  getServiceLaneDropdownsState,
  (state: ServiceLaneDropdownsState) => state.declinedReasons
);
const declinedReasonsSelectors = declinedReasonsAdapter.getSelectors(declinedReasons);

const serviceAdvisors = createSelector(employeesSelectors.selectAll, (state: Employee[]) => {
  return state.filter((emp) => {
    return emp.jobTitleId == 'SAD';
  });
});
const technicians = createSelector(employeesSelectors.selectAll, (state: Employee[]) => {
  return state.filter((emp) => {
    return emp.jobTitleId == 'TCH';
  });
});

const technicianGroups = createSelector(
  getServiceLaneDropdownsState,
  (state: ServiceLaneDropdownsState) => state.technicianGroups
);
const technicianGroupsSelectors = technicianGroupsAdapter.getSelectors(technicianGroups);

const vehicleColours = createSelector(
  getServiceLaneDropdownsState,
  (state: ServiceLaneDropdownsState) => state.vehicleColours
);
const vehicleColoursSelectors = vehicleColourAdapter.getSelectors(vehicleColours);

const vehicleSubTypes = createSelector(
  getServiceLaneDropdownsState,
  (state: ServiceLaneDropdownsState) => state.vehicleSubTypes
);
const vehicleSubTypesSelectors = vehicleSubTypeAdapter.getSelectors(vehicleSubTypes);

const submissionTypes = createSelector(
  getServiceLaneDropdownsState,
  (state: ServiceLaneDropdownsState) => state.warrantySubmissionTypes
);
const submissionTypesSelectors = submissionTypeAdapter.getSelectors(submissionTypes);

const filteredSubmissionTypes = createSelector(
  submissionTypesSelectors.selectAll,
  (state: WarrantySubmissionType[]) => {
    return state.filter((st: WarrantySubmissionType) => {
      return st.hiddenFlag == false;
    });
  }
);

const vehicleMakes = createSelector(
  getServiceLaneDropdownsState,
  (state: ServiceLaneDropdownsState) => state.vehicleMakes
);
const vehicleMakesSelectors = vehicleMakesAdapter.getSelectors(vehicleMakes);

const vehicleInspectionWearPatterns = createSelector(
  getServiceLaneDropdownsState,
  (state: ServiceLaneDropdownsState) => state.vehicleInspectionWearPatterns
);
const vehicleInspectionWearPatternsSelectors =
  vehicleInspectionWearPatternsAdapter.getSelectors(vehicleInspectionWearPatterns);

export const getAllDropdowns = createSelector(
  messageTypeSelectors.selectAll,
  provinceStateSelectors.selectAll,
  employeesSelectors.selectAll,
  technicianGroupsSelectors.selectAll,
  vehicleColoursSelectors.selectAll,
  vehicleSubTypesSelectors.selectAll,
  locationSelectors.selectAll,
  serviceCodeSelectors.selectAll,
  (
    messageTypes: MessageType[],
    provinceStates: ProvinceState[],
    employees: Employee[],
    technicianGroups: TechnicianGroup[],
    vehicleColours,
    vehicleSubTypes,
    locations,
    serviceCodes
  ) => {
    return {
      messageTypes,
      provinceStates,
      employees,
      technicianGroups,
      vehicleColours,
      vehicleSubTypes,
      locations,
      serviceCodes,
    };
  }
);

export const getServiceAppointmentCaseDropdowns = createSelector(
  technicianGroupsSelectors.selectAll,
  filteredSubmissionTypes,
  serviceCodeSelectors.selectAll,
  technicians,
  (
    technicianGroups: TechnicianGroup[],
    submissionTypes: WarrantySubmissionType[],
    serviceCodes: ServiceCode[],
    technicians: Employee[]
  ) => {
    return {
      technicianGroups,
      submissionTypes,
      serviceCodes,
      technicians,
    };
  }
);

export const getVehicleDropdowns = createSelector(
  vehicleColoursSelectors.selectAll,
  vehicleSubTypesSelectors.selectAll,
  vehicleMakesSelectors.selectAll,
  vehicleInspectionWearPatternsSelectors.selectAll,
  (
    colours: Colour[],
    subTypes: SubType[],
    makes: VehicleMake[],
    inspectionWearPatterns: VehicleInspectionWearPattern[]
  ) => {
    return {
      colours,
      subTypes,
      makes,
      inspectionWearPatterns,
    };
  }
);

export const getAssociateDropdowns = createSelector(
  messageTypeSelectors.selectAll,
  associateClassSelectors.selectAll,
  (messageTypes: MessageType[], associateClasses: Class[]) => {
    return {
      messageTypes,
      associateClasses,
    };
  }
);

export const getAddressDropdowns = createSelector(
  locationSelectors.selectAll,
  provinceStateSelectors.selectAll,
  (locations: Location[], provinceStates: ProvinceState[]) => {
    return {
      locations,
      provinceStates,
    };
  }
);

export const getServiceAppointmentDropdowns = createSelector(
  employeesSelectors.selectAll,
  technicianGroupsSelectors.selectAll,
  serviceAdvisors,
  activeServiceCodes,
  technicians,
  messageTypeSelectors.selectAll,
  transportOptionsSelectors.selectAll,
  provinceStateSelectors.selectAll,
  (
    employees: Employee[],
    technicianGroups: TechnicianGroup[],
    serviceAdvisors: Employee[],
    serviceCodes: ServiceCode[],
    technicians: Employee[],
    messageTypes: MessageType[],
    transportOptions: TransportOption[],
    provinceStates: ProvinceState[]
  ) => {
    return {
      employees,
      technicianGroups,
      serviceAdvisors,
      serviceCodes,
      technicians,
      messageTypes,
      transportOptions,
      provinceStates,
    };
  }
);

export const serviceLaneDropdownsQuery = {
  getAllDropdowns,
  messageTypes: messageTypeSelectors.selectAll,
  getServiceAppointmentDropdowns,
  getServiceAppointmentCaseDropdowns,
  getVehicleDropdowns,
  getAssociateDropdowns,
  getAddressDropdowns,
  locations: locationSelectors.selectAll,
  directMessageTemplates: directMessageTemplatesSelectors.selectAll,
  serviceCodes: activeServiceCodes,
  declinedReasons: declinedReasonsSelectors.selectAll,
  vehicleInspectionWearPatterns: vehicleInspectionWearPatternsSelectors.selectAll,
  serviceAdvisors,
};
